import { useState, useEffect, useRef } from 'react';
import { NoRecords, PageLoader } from '../../../lib/loader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import moment from 'moment';
import { useGetAdminEditHistory, } from '../api/apiReferral';
import { ArrowUp, ArrowDown, Search, Eye, X, ArrowRight } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useCheckAdminAccess } from '../../WhiteIp/api/whiteipapi';
import { logout } from '../../../hooks/logout';
import PermissionDenied from '../../common/permissionDenied';
import { WaitingLoader } from '../../../hooks/buttonLoad';
import { useBreadcrumbPagination } from '../../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../../utils/BreadcrumbPagination';
import { useDebounce } from '../../../hooks/debounce';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 800,
    bgcolor: 'background.paper',
    borderRadius: '12px',
    boxShadow: 24,
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f5f5f5'
};

const contentStyle = {
    padding: '24px',
    overflow: 'auto',
    flex: 1
};

const footerStyle = {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: '#f5f5f5'
};

function ReferralEdit() {

    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const [search, setSearch] = useState("");
    const searchText = useDebounce(search);
    const [sortOrder, setSortOrder] = useState("desc");
    const { id } = useParams();

    // Modal States
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);

    const { data: getCashbackHistory, isLoading } = useGetAdminEditHistory({
        id,
        page: searchText ? 0 : tempPage,
        size: tempRows,
        level: '',
        searchText,
        sortField: "createdAt",
        sortOrder
    });

    const totalRecords = getCashbackHistory?.data?.totalRecords || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    });

    useEffect(() => {
        setTempPage(0);
        if (handleChangePage) {
            handleChangePage(null, 0);
        }
    }, [searchText]);

    const handleOpenModal = (row) => {
        setSelectedRowData(row);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRowData(null);
    };



    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();
    }, []);



    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'Referral Management' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };



    const DetailRow = ({ label, previousValue, currentValue, isChanged }) => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #f0f0f0',
            '&:last-child': { borderBottom: 'none' }
        }}>
            <Box sx={{ width: '25%', fontWeight: 600, color: '#555' }}>
                {label}
            </Box>
            <Box sx={{
                width: '30%',
                padding: '8px 12px',
                backgroundColor: isChanged ? '#ffebee' : '#f5f5f5',
                borderRadius: '6px',
                color: isChanged ? '#c62828' : '#333'
            }}>
                {previousValue || '-'}
            </Box>
            <Box sx={{ width: '10%', display: 'flex', justifyContent: 'center' }}>
                <ArrowRight size={20} color="#666" />
            </Box>
            <Box sx={{
                width: '30%',
                padding: '8px 12px',
                backgroundColor: isChanged ? '#e8f5e9' : '#f5f5f5',
                borderRadius: '6px',
                color: isChanged ? '#2e7d32' : '#333'
            }}>
                {currentValue || '-'}
            </Box>
        </Box>
    );

    const DetailViewModal = () => {
        if (!selectedRowData) return null;

        const previousData = selectedRowData.previousData || selectedRowData.oldData || {};
        const currentData = selectedRowData.currentData || selectedRowData.newData || {};

        const allKeys = [...new Set([
            ...Object.keys(previousData),
            ...Object.keys(currentData)
        ])];

        const formatFieldName = (key) => {
            return key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .replace(/_/g, ' ');
        };

        const hasChanged = (key) => {
            return JSON.stringify(previousData[key]) !== JSON.stringify(currentData[key]);
        };

        const formatValue = (value) => {
            if (value === null || value === undefined) return '-';
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
        };

        return (
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="edit-history-modal"
                aria-describedby="edit-history-details"
            >
                <Box sx={modalStyle}>
                    <Box sx={headerStyle}>
                        <Box>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                Edit History Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {moment(selectedRowData?.createdAt).format("DD-MM-YYYY | hh:mm A")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                <strong>Edited by:</strong> {selectedRowData?.admin?.name || "Admin"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                <strong>E-mail:</strong> {selectedRowData?.admin?.email || "Admin"}
                            </Typography>
                        </Box>

                        <IconButton onClick={handleCloseModal} size="small">
                            <X size={24} />
                        </IconButton>
                    </Box>

                    <Box sx={contentStyle}>


                        <Box>


                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 0',
                                borderBottom: '2px solid #e0e0e0',
                                backgroundColor: '#fafafa',
                                borderRadius: '8px 8px 0 0',
                                marginBottom: 1
                            }}>
                                <Box sx={{ width: '25%', fontWeight: 700, color: '#333', paddingLeft: 1 }}>
                                    Field
                                </Box>
                                <Box sx={{ width: '30%', fontWeight: 700, color: '#c62828', textAlign: 'center' }}>
                                    Previous Value
                                </Box>
                                <Box sx={{ width: '10%' }}></Box>
                                <Box sx={{ width: '30%', fontWeight: 700, color: '#2e7d32', textAlign: 'center' }}>
                                    Current Value
                                </Box>
                            </Box>

                            <Box sx={{
                                backgroundColor: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '0 0 8px 8px',
                                padding: '0 12px'
                            }}>
                                {allKeys.length > 0 ? (
                                    allKeys.map((key, index) => (
                                        <DetailRow
                                            key={index}
                                            label={formatFieldName(key)}
                                            previousValue={formatValue(previousData[key])}
                                            currentValue={formatValue(currentData[key])}
                                            isChanged={hasChanged(key)}
                                        />
                                    ))
                                ) : (
                                    <>
                                        {selectedRowData?.data?.previousData?.rewardpercent &&
                                            <DetailRow
                                                label="Reward Percentage"
                                                previousValue={selectedRowData?.data?.previousData?.rewardpercent}
                                                currentValue={selectedRowData?.data?.updateData?.rewardpercent}
                                                isChanged={selectedRowData?.data?.previousData?.rewardpercent !== selectedRowData?.data?.updateData?.rewardpercent}
                                            />
                                        }
                                        {selectedRowData?.data?.previousData?.refUserCount &&
                                            <DetailRow
                                                label="Level Eligiblity"
                                                previousValue={selectedRowData?.data?.previousData?.refUserCount}
                                                currentValue={selectedRowData?.data?.updateData?.refUserCount}
                                                isChanged={selectedRowData?.data?.previousData?.refUserCount !== selectedRowData?.data?.updateData?.refUserCount}
                                            />
                                        }

                                        {selectedRowData?.data?.previousData?.rWdSwTbH &&
                                            <DetailRow
                                                label="Card Bonus Reward"
                                                previousValue={selectedRowData?.data?.previousData?.rWdSwTbH}
                                                currentValue={selectedRowData?.data?.updateData?.rWdSwTbH}
                                                isChanged={selectedRowData?.data?.previousData?.rWdSwTbH !== selectedRowData?.data?.updateData?.rWdSwTbH}
                                            />
                                        }
                                        {selectedRowData?.data?.previousData?.maxEligibleAmnt &&
                                            <DetailRow
                                                label="Maximum Eligible Amount"
                                                previousValue={selectedRowData?.data?.previousData?.maxEligibleAmnt}
                                                currentValue={selectedRowData?.data?.updateData?.maxEligibleAmnt}
                                                isChanged={selectedRowData?.data?.previousData?.maxEligibleAmnt !== selectedRowData?.data?.updateData?.maxEligibleAmnt}
                                            />
                                        }

                                        {selectedRowData?.data?.previousData?.acdEnautga &&
                                            <DetailRow
                                                label="Minimum Top‑Up Amount "
                                                previousValue={selectedRowData?.data?.previousData?.acdEnautga}
                                                currentValue={selectedRowData?.data?.updateData?.acdEnautga}
                                                isChanged={selectedRowData?.data?.previousData?.acdEnautga !== selectedRowData?.data?.updateData?.acdEnautga}
                                            />
                                        }

                                        {selectedRowData?.data?.previousData?.isRePmCvReWdf?.toString() &&
                                            <DetailRow
                                                label="Referral Link Generation"
                                                previousValue={selectedRowData?.data?.previousData?.isRePmCvReWdf ? "Top up" : "Card Creation"}
                                                currentValue={selectedRowData?.data?.updateData?.isRePmCvReWdf ? "Top up" : "Card Creation"}
                                                isChanged={selectedRowData?.data?.previousData?.isRePmCvReWdf !== selectedRowData?.data?.updateData?.isRePmCvReWdf}
                                            />
                                        }
                                    </>
                                )}
                            </Box>

                        </Box>


                    </Box>

                    {/* Footer */}
                    <Box sx={footerStyle}>
                        <Button
                            variant="contained"
                            onClick={handleCloseModal}
                            sx={{ textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
        );
    };

    const renderTotalReferralhistoryTable = () => (
        getCashbackHistory?.data?.adminEditHis?.length > 0 ? (
            <>
                <div style={{ minHeight: "60vh" }}>
                    <div className='table-responsive'>
                        <Table sx={{ minWidth: 650 }} aria-label="Referral transaction history table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className='text-nowrap' align="center">S.NO</TableCell>
                                    <TableCell className='text-nowrap' align="center">Browser</TableCell>
                                    <TableCell className='text-nowrap' align="center">Device</TableCell>
                                    <TableCell className='text-nowrap' align="center">IP</TableCell>
                                    <TableCell className='text-nowrap' align="center">Location</TableCell>
                                    <TableCell className='text-nowrap' align="center">
                                        Date & Time{' '}
                                        {sortOrder !== 'desc' ? (
                                            <ArrowUp
                                                onClick={() => setSortOrder('desc')}
                                                style={{ cursor: 'pointer' }}
                                                aria-label="Sort descending"
                                            />
                                        ) : (
                                            <ArrowDown
                                                onClick={() => setSortOrder('asc')}
                                                style={{ cursor: 'pointer' }}
                                                aria-label="Sort ascending"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align='center'>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getCashbackHistory?.data?.adminEditHis?.map((row, i) => (
                                    <TableRow key={row?._id || i} hover>
                                        <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                                        <TableCell align="center">{row.browser || '-'}</TableCell>
                                        <TableCell align="center">{row.device || '-'}</TableCell>
                                        <TableCell align="center">{row.ip || '-'}</TableCell>
                                        <TableCell align="center">{row.location || '-'}</TableCell>
                                        <TableCell align="center">
                                            {moment(row?.createdAt).format("DD-MM-YYYY | hh:mm A")}
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: "nowrap" }} align="center">
                                            <Eye
                                                size={20}
                                                style={{ cursor: 'pointer', color: '#1976d2' }}
                                                onClick={() => handleOpenModal(row)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </>
        ) : (
            <NoRecords />
        )
    );

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        content = (
            <section>
                <h4>Admin Reward Update History</h4>
                <div className="ad_pg_bx bg-white p-3">

                    {isLoading ? <PageLoader /> : renderTotalReferralhistoryTable()}
                </div>

                <DetailViewModal />
            </section>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;
}

export default ReferralEdit;



